import { EPSILON, vAdd, vDist, vMul, vNorm, vPerp, vSub } from './geometry.js';
import type { DrawPoint, Vec } from './types.js';

export interface StrokeOptions {
  /** Nominal stroke width in board units. */
  size: number;
  /** How strongly pressure modulates width, 0 disables it. */
  thinning: number;
  /** 0..1, how much to smooth the centre line. */
  smoothing: number;
  /** Length of the entry taper, in board units. */
  taperStart: number;
  /** Length of the exit taper, in board units. */
  taperEnd: number;
  /** Round the ends instead of tapering to a point. */
  capStart: boolean;
  capEnd: boolean;
}

export const DEFAULT_STROKE_OPTIONS: StrokeOptions = {
  size: 6,
  thinning: 0.55,
  smoothing: 0.58,
  taperStart: 0,
  taperEnd: 0,
  capStart: true,
  capEnd: true,
};

export const BRUSH_PRESETS: Record<string, Partial<StrokeOptions>> = {
  // Round caps, no end taper — pointed tips made handwriting look like spikes.
  //
  // Pen: pressure is what a nib is for, so it gets the most of it, and a
  // little less smoothing than before so the corners of handwriting survive.
  // Marker: felt gives slightly under pressure, so a touch of thinning keeps
  // it from reading as a flat vector line, and heavier smoothing suits its
  // broad, deliberate strokes. Highlighter: a constant-width band, smoothed
  // hardest of all — it is swept, not written.
  pen: { thinning: 0.62, smoothing: 0.5, taperStart: 0, taperEnd: 0, capStart: true, capEnd: true },
  marker: { thinning: 0.18, smoothing: 0.62, taperStart: 0, taperEnd: 0, capStart: true, capEnd: true },
  highlighter: { thinning: 0, smoothing: 0.75, taperStart: 0, taperEnd: 0, capStart: true, capEnd: true },
};

interface Sample {
  point: Vec;
  pressure: number;
  /** Cumulative arc length up to this sample. */
  distance: number;
  vector: Vec;
}

function mixPoints(a: DrawPoint, b: DrawPoint, t: number): DrawPoint {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u];
}

function knotAt(ti: number, a: DrawPoint, b: DrawPoint): number {
  return ti + Math.sqrt(Math.max(Math.hypot(b[0] - a[0], b[1] - a[1]), EPSILON));
}

/**
 * Centripetal Catmull-Rom through p1→p2. Unlike a uniform spline it does not
 * overshoot on tight handwriting corners (the loop on a handwritten "l").
 */
function centripetal(p0: DrawPoint, p1: DrawPoint, p2: DrawPoint, p3: DrawPoint, t: number): DrawPoint {
  const t0 = 0;
  const t1 = knotAt(t0, p0, p1);
  const t2 = knotAt(t1, p1, p2);
  const t3 = knotAt(t2, p2, p3);
  const tInterp = t1 + (t2 - t1) * t;

  const span = (a: number, b: number) => (b - a < EPSILON ? 0 : (tInterp - a) / (b - a));
  const a1 = mixPoints(p0, p1, span(t0, t1));
  const a2 = mixPoints(p1, p2, span(t1, t2));
  const a3 = mixPoints(p2, p3, span(t2, t3));
  const b1 = mixPoints(a1, a2, span(t0, t2));
  const b2 = mixPoints(a2, a3, span(t1, t3));
  return mixPoints(b1, b2, span(t1, t2));
}

function atClamped(points: readonly DrawPoint[], i: number): DrawPoint {
  if (i < 0) return points[0]!;
  if (i >= points.length) return points[points.length - 1]!;
  return points[i]!;
}

/**
 * Fills large pointer gaps with a curve through the captured samples. Fast
 * writing otherwise becomes a polygon whose facets depend on how the OS
 * coalesced events that frame.
 */
function densify(points: readonly DrawPoint[], spacing: number): DrawPoint[] {
  if (points.length < 2) return points.slice();

  const out: DrawPoint[] = [points[0]!];
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const dist = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    if (dist < EPSILON) continue;

    const steps = Math.min(24, Math.max(1, Math.round(dist / spacing)));
    if (steps === 1) {
      out.push(p2);
      continue;
    }

    const p0 = atClamped(points, i - 1);
    const p3 = atClamped(points, i + 2);
    for (let k = 1; k <= steps; k++) {
      out.push(k === steps ? p2 : centripetal(p0, p1, p2, p3, k / steps));
    }
  }
  return out;
}

/**
 * Gaussian blur along the polyline, in board units rather than sample count.
 * A per-sample lerp used to over-smooth slow, dense writing and under-smooth
 * fast, sparse writing of the same letter.
 */
function smoothAlongPath(points: readonly DrawPoint[], sigma: number): DrawPoint[] {
  const n = points.length;
  if (n < 3 || sigma <= 0) return points.slice();

  const dist = new Float64Array(n);
  for (let i = 1; i < n; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    dist[i] = dist[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]);
  }

  const radius = 3 * sigma;
  const twoSigma2 = 2 * sigma * sigma;
  const out: DrawPoint[] = new Array(n);
  out[0] = points[0]!;
  out[n - 1] = points[n - 1]!;

  let lo = 0;
  let hi = 0;
  for (let i = 1; i < n - 1; i++) {
    const center = dist[i]!;
    while (lo < i && center - dist[lo]! > radius) lo++;
    if (hi < i) hi = i;
    while (hi + 1 < n && dist[hi + 1]! - center <= radius) hi++;

    let x = 0;
    let y = 0;
    let p = 0;
    let w = 0;
    for (let j = lo; j <= hi; j++) {
      const d = dist[j]! - center;
      const wt = Math.exp(-(d * d) / twoSigma2);
      const s = points[j]!;
      x += s[0] * wt;
      y += s[1] * wt;
      p += s[2] * wt;
      w += wt;
    }
    out[i] = [x / w, y / w, p / w];
  }
  return out;
}

/**
 * Turns raw pointer samples into a centre line whose smoothness does not
 * depend on how fast the pen was moving or how densely the OS sampled it.
 */
function prepareStroke(points: readonly DrawPoint[], opts: StrokeOptions): DrawPoint[] {
  if (points.length < 2) return points.slice();
  const spacing = Math.max(opts.size * 0.32, 0.6);
  const densified = densify(points, spacing);
  if (opts.smoothing <= 0) return densified;
  const sigma = opts.size * opts.smoothing * 0.42;
  return smoothAlongPath(densified, sigma);
}

function toSamples(points: readonly DrawPoint[], opts: StrokeOptions): Sample[] {
  const prepared = prepareStroke(points, opts);
  const out: Sample[] = [];
  let total = 0;
  let prev: Vec | null = null;
  let prevVec: Vec = { x: 1, y: 0 };

  for (let i = 0; i < prepared.length; i++) {
    const raw = prepared[i]!;
    const p: Vec = { x: raw[0], y: raw[1] };
    if (prev) {
      const d = vDist(p, prev);
      if (d < EPSILON) continue;
      total += d;
      prevVec = vNorm(vSub(p, prev));
    }
    out.push({ point: p, pressure: raw[2], distance: total, vector: prevVec });
    prev = p;
  }

  if (out.length > 1) out[0]!.vector = out[1]!.vector;
  return out;
}

function radiusAt(sample: Sample, opts: StrokeOptions, totalLength: number): number {
  const base = opts.size / 2;
  const pressure = opts.thinning > 0 ? sample.pressure : 1;
  let r = base * (1 - opts.thinning + opts.thinning * pressure);

  if (opts.taperStart > 0) {
    const t = Math.min(1, sample.distance / opts.taperStart);
    r *= Math.sin((t * Math.PI) / 2);
  }
  if (opts.taperEnd > 0) {
    const remaining = totalLength - sample.distance;
    const t = Math.min(1, remaining / opts.taperEnd);
    r *= Math.sin((t * Math.PI) / 2);
  }
  return Math.max(r, 0.1);
}

/**
 * Semicircle from `from` to `to` around `center`, swept the way that bulges
 * along `outward` — otherwise the arc folds back through the stroke and the
 * self-intersection paints as a white pin-hole on the tip.
 */
function capArc(center: Vec, from: Vec, to: Vec, outward: Vec, steps: number): Vec[] {
  const startAngle = Math.atan2(from.y - center.y, from.x - center.x);
  let endAngle = Math.atan2(to.y - center.y, to.x - center.x);
  let delta = endAngle - startAngle;
  while (delta <= 0) delta += Math.PI * 2;
  const midA = startAngle + delta / 2;
  const midB = startAngle + (delta - Math.PI * 2) / 2;
  const dotA = Math.cos(midA) * outward.x + Math.sin(midA) * outward.y;
  const dotB = Math.cos(midB) * outward.x + Math.sin(midB) * outward.y;
  const useDelta = dotA >= dotB ? delta : delta - Math.PI * 2;
  const radius = vDist(center, from);
  const out: Vec[] = [];
  for (let i = 1; i < steps; i++) {
    const a = startAngle + (i / steps) * useDelta;
    out.push({ x: center.x + Math.cos(a) * radius, y: center.y + Math.sin(a) * radius });
  }
  return out;
}

/**
 * Turns a pressure-sampled centre line into a closed outline polygon.
 *
 * This is the same idea as perfect-freehand, reimplemented here so the package
 * stays dependency-free and so the outline can be produced identically on the
 * server for PNG/PDF export.
 */
export function strokeOutline(
  points: readonly DrawPoint[],
  options: Partial<StrokeOptions> = {},
): Vec[] {
  const opts: StrokeOptions = { ...DEFAULT_STROKE_OPTIONS, ...options };
  const samples = toSamples(points, opts);

  if (samples.length === 0) return [];

  if (samples.length === 1) {
    // A tap: emit a circle so single dots are visible.
    const s = samples[0]!;
    const r = radiusAt(s, opts, 0);
    const circle: Vec[] = [];
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      circle.push({ x: s.point.x + Math.cos(a) * r, y: s.point.y + Math.sin(a) * r });
    }
    return circle;
  }

  const totalLength = samples[samples.length - 1]!.distance;
  const left: Vec[] = [];
  const right: Vec[] = [];

  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    const r = radiusAt(s, opts, totalLength);
    // Average the incoming and outgoing directions so corners do not pinch.
    const next = samples[i + 1];
    const dir = next ? vNorm(vAdd(s.vector, next.vector)) : s.vector;
    const n = vPerp(dir.x === 0 && dir.y === 0 ? s.vector : dir);
    left.push(vAdd(s.point, vMul(n, r)));
    right.push(vSub(s.point, vMul(n, r)));
  }

  const first = samples[0]!;
  const last = samples[samples.length - 1]!;
  const leftLast = left[left.length - 1]!;
  const rightLast = right[right.length - 1]!;
  const leftFirst = left[0]!;
  const rightFirst = right[0]!;

  // End cap: left → right, bulging past the tip. Start cap: right → left
  // (after the reversed right edge), bulging before the first point.
  const endCap = opts.capEnd
    ? capArc(last.point, leftLast, rightLast, last.vector, 12)
    : [];
  const startCap = opts.capStart
    ? capArc(first.point, rightFirst, leftFirst, vMul(first.vector, -1), 12)
    : [];

  return [...left, ...endCap, ...right.reverse(), ...startCap];
}

/**
 * Smoothed centre line for live preview and for strokes too thin to fill an
 * outline. Uses the same prepare pass as `strokeOutline`, so the ink does not
 * change shape when the pointer lifts.
 */
export function strokeCenterline(
  points: readonly DrawPoint[],
  options: Partial<StrokeOptions> = {},
): Vec[] {
  const opts: StrokeOptions = { ...DEFAULT_STROKE_OPTIONS, ...options };
  return prepareStroke(points, opts).map((p) => ({ x: p[0], y: p[1] }));
}

/** Average pressure, used to pick a representative width for the live preview. */
export function averagePressure(points: readonly DrawPoint[]): number {
  if (points.length === 0) return 0.5;
  let sum = 0;
  for (const p of points) sum += p[2];
  return sum / points.length;
}
